/**
 * "Which brand do you use?" — pick among the APPROVED brands for one
 * ingredient. Applies immediately: every attached brand option is already a
 * chef-approved substitution, so choosing between them needs no approval.
 *
 * Deliberately NOT the substitution flow. Substituting means serving a
 * different INGREDIENT, which still needs review; this only records which of
 * several approved SKUs of the same ingredient the kitchen actually buys, and
 * crediting + nutrition follow the answer.
 *
 * Shape follows the surface: a bottom drawer on a tablet or phone in the
 * kitchen, a popover on a desktop. A control appears ONLY when there are two
 * or more brands — see `brandControlMode`.
 */
import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { DatePicker } from '@ordo/ui/date-picker'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@ordo/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Calendar as CalendarIcon, Check, ChevronDown } from 'lucide-react'
import { type ComponentPropsWithRef, type ReactNode, useState } from 'react'

import {
  type KitchenBrandOption,
  useKitchenBrandOptionsQuery,
  useSelectKitchenBrandMutation,
} from '@/api/kitchen-brand-selections'
import { useActiveKitchen } from '@/hooks/chefs/useActiveKitchen'
import { brandControlMode } from '@/lib/chefs/brand-control'
import { useIsMobile } from '@/lib/use-is-mobile'

/** Rows are finger-sized: this is used standing at a bench, not at a desk. */
const ROW = 'flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-3 text-left text-sm hover:bg-muted'

/**
 * The slice of an ingredient row the picker needs. Structural on purpose:
 * recipe lines (`Ingredient` in lib/chefs/types) and shopping-list rows both
 * satisfy it, so one control serves every chef ingredient surface.
 */
export interface BrandPickerIngredient {
  name: string
  brand?: string | null
  ingredientId?: string | null
  brandOptionId?: string | null
  brandSource?: 'PINNED' | 'KITCHEN' | 'VARIETY' | 'DEFAULT' | null
  brandSelectable?: boolean
  brandOptionCount?: number
}

export function IngredientBrandPicker({ ingredient }: { ingredient: BrandPickerIngredient }) {
  // Same source the substitution modal uses — no prop-drilling through every
  // recipe surface that renders an ingredient list.
  const kitchenLocationId = useActiveKitchen()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  // Chefs commonly lock a brand in for a school year, but the common case is
  // "from now on" — so dates stay collapsed until asked for. Rendered as a
  // separate VIEW rather than inline, so the calendar's own popover is never
  // nested inside this one.
  const [showDates, setShowDates] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const select = useSelectKitchenBrandMutation()
  // Only fetched once opened — most rows are never touched.
  const options = useKitchenBrandOptionsQuery(kitchenLocationId, ingredient.ingredientId ?? null, { enabled: open })

  const mode = brandControlMode({
    brandOptionCount: ingredient.brandOptionCount,
    brandSelectable: ingredient.brandSelectable,
    hasBrandLabel: Boolean(ingredient.brand),
    hasIngredientId: Boolean(ingredient.ingredientId),
    hasKitchen: Boolean(kitchenLocationId),
  })

  if (mode === 'none') {
    return null
  }

  // One brand, no brands, or a pinned line: settled. Say what it is and stop.
  if (mode === 'text') {
    return (
      <span className="truncate text-xs text-muted-foreground">
        {ingredient.brand}
        {ingredient.brandSource === 'PINNED' ? ' · specified for this recipe' : ''}
      </span>
    )
  }

  const list = options.data?.options ?? []
  const busy = select.isPending

  function closeAll() {
    setOpen(false)
    setShowDates(false)
  }

  async function save(brandOptionId: string) {
    if (!kitchenLocationId || !ingredient.ingredientId) {
      return
    }
    const chosen = list.find((option) => option.id === brandOptionId)
    try {
      await select.mutateAsync({
        kitchenLocationId,
        ingredientId: ingredient.ingredientId,
        brandOptionId,
        // Blank stays null — an open-ended "from now on" choice.
        startDate: startDate || null,
        endDate: endDate || null,
      })
      // The row only updates once the orders query refetches; say so now so a
      // slow kitchen connection doesn't read as "nothing happened".
      toast.success(`${chosen?.label ?? 'Brand'} saved for ${ingredient.name}`)
      closeAll()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save that brand.')
    }
  }

  const body = (
    <div className="flex flex-col gap-1 p-1">
      {showDates ? (
        <DateView
          startDate={startDate}
          endDate={endDate}
          onStart={setStartDate}
          onEnd={setEndDate}
          onBack={() => setShowDates(false)}
          // An existing selection can have its window changed without picking
          // the same brand again.
          onSave={ingredient.brandOptionId ? () => void save(ingredient.brandOptionId!) : null}
          busy={busy}
        />
      ) : (
        <>
          <p className="px-3 py-1.5 text-xs text-muted-foreground">
            Which brand do you use for {ingredient.name}? Applies to every recipe using it.
          </p>
          {ingredient.brandSource === 'VARIETY' ? (
            <p className="px-3 pb-1.5 text-xs text-muted-foreground">
              This line allows any approved brand — choosing one means crediting follows that brand instead of the worst
              case.
            </p>
          ) : null}
          {options.isPending ? (
            <div className="space-y-1 p-2">
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : (
            list.map((option) => (
              <BrandRow
                key={option.id}
                option={option}
                selected={ingredient.brandOptionId === option.id}
                disabled={busy}
                onSelect={() => void save(option.id)}
              />
            ))
          )}
          <div className="mt-1 border-t pt-1">
            <Button
              type="button"
              variant="ghost"
              className="min-h-11 w-full justify-start text-xs"
              onClick={() => setShowDates(true)}>
              <CalendarIcon className="mr-2 h-4 w-4" strokeWidth={1.75} />
              {startDate || endDate ? 'Dates set — review' : 'Only for certain dates — e.g. this school year'}
            </Button>
          </div>
        </>
      )}
    </div>
  )

  const triggerLabel = ingredient.brand ?? 'Choose a brand'

  if (isMobile) {
    return (
      <>
        <BrandTrigger label={triggerLabel} source={ingredient.brandSource} onClick={() => setOpen(true)} />
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent>
            <DrawerHeader className="pb-0">
              <DrawerTitle className="text-base">{ingredient.name}</DrawerTitle>
            </DrawerHeader>
            <div className="max-h-[60vh] overflow-y-auto pb-4">{body}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setShowDates(false)
        }
      }}>
      <PopoverTrigger asChild>
        <BrandTrigger label={triggerLabel} source={ingredient.brandSource} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        {body}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Visible, finger-sized affordance — the old hover-underline didn't exist on
 * touch. Spreads the rest props (and the React 19 ref-as-prop) onto the
 * button: on desktop this sits inside `<PopoverTrigger asChild>`, and Radix's
 * Slot-injected ref is what anchors the popover — dropping it leaves the
 * popover unpositioned off-screen.
 */
function BrandTrigger({
  label,
  source,
  className,
  ...props
}: {
  label: string
  source: BrandPickerIngredient['brandSource']
} & ComponentPropsWithRef<'button'>) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'inline-flex min-h-8 max-w-full items-center gap-1 rounded-md border border-border px-2 py-0.5',
        'text-xs text-muted-foreground hover:bg-muted hover:text-foreground',
        className,
      )}>
      <span className="truncate">{label}</span>
      {source === 'KITCHEN' ? <span className="shrink-0 text-primary">· kitchen pick</span> : null}
      <ChevronDown className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
    </button>
  )
}

/** Exported for tests — the two-line SKU row is pinned by
 *  `test/components/chefs/ingredient-brand-picker.test.tsx`. */
export function BrandRow({
  option,
  selected,
  disabled,
  onSelect,
}: {
  option: KitchenBrandOption
  selected: boolean
  disabled: boolean
  onSelect: () => void
}): ReactNode {
  return (
    <button type="button" disabled={disabled} onClick={onSelect} className={cn(ROW, 'disabled:opacity-50')}>
      {/* Several approved SKUs can share a brand ("Tyson" × 3), so the brand
          alone can't be the whole row: the detail line carries the product
          name, size, and package. Everything wraps — never ellipsize
          identifying info a chef needs to tell SKUs apart. */}
      <span className="flex flex-col py-2">
        <span>
          {option.title}
          {option.isDefault ? <span className="text-muted-foreground"> (standard)</span> : null}
        </span>
        {option.detail ? <span className="text-xs text-muted-foreground">{option.detail}</span> : null}
      </span>
      {selected ? <Check className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} /> : null}
    </button>
  )
}

/** Separate view, not a nested popover: the calendar owns its own overlay and
 *  dismissing it must not tear down the brand list underneath. */
function DateView({
  startDate,
  endDate,
  onStart,
  onEnd,
  onBack,
  onSave,
  busy,
}: {
  startDate: string
  endDate: string
  onStart: (value: string) => void
  onEnd: (value: string) => void
  onBack: () => void
  onSave: (() => void) | null
  busy: boolean
}) {
  // `DatePicker` renders its own trigger button and takes no id, so a <label
  // htmlFor> pointed at nothing — and the ids were static, so a recipe emitted
  // one duplicate pair per ingredient row. A labelled group names the control
  // without inventing an id that doesn't exist.
  return (
    <div className="space-y-3 p-3">
      <p className="text-xs text-muted-foreground">Leave a date blank for open-ended.</p>
      <div className="space-y-1" role="group" aria-label="Start date">
        <p className="text-xs font-medium">From</p>
        <DatePicker value={startDate} onChange={onStart} placeholder="Today" />
      </div>
      <div className="space-y-1" role="group" aria-label="End date">
        <p className="text-xs font-medium">Until</p>
        <DatePicker value={endDate} onChange={onEnd} placeholder="No end date" />
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" className="min-h-11 flex-1" onClick={onBack}>
          Back
        </Button>
        {onSave ? (
          <Button type="button" size="sm" disabled={busy} className="min-h-11 flex-1" onClick={onSave}>
            Save dates
          </Button>
        ) : null}
      </div>
      {onSave ? null : <p className="text-xs text-muted-foreground">Pick a brand to apply these dates.</p>}
    </div>
  )
}
